// Dense, elegant KPI card — the building block of the Executive Cockpit.
// High information density, not oversized. Tone shown by a bar on the left;
// the value stays ink unless it needs attention (warning/danger). Brand colours only.

import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import type { Kpi, KpiTone } from '../kpis';
import { UI } from '../../brand/ui';

const toneColor: Record<KpiTone, string> = {
  positive: UI.accent,
  negative: UI.danger,
  warning: UI.warning,
  neutral: UI.border
};

export function KpiCard({ kpi, loading }: { kpi: Kpi; loading?: boolean }) {
  if (loading) {
    return (
      <Card sx={{ borderTop: `3px solid`, borderColor: 'divider' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Skeleton variant="text" width="60%" height={16} />
          <Skeleton variant="text" width="80%" height={32} sx={{ mt: 0.5 }} />
          <Skeleton variant="text" width="40%" height={14} />
        </CardContent>
      </Card>
    );
  }

  const accent = toneColor[kpi.tone ?? 'neutral'];

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderLeft: `3px solid ${accent}`,
        transition: 'box-shadow 0.18s ease',
        '&:hover': { boxShadow: UI.shadowMd }
      }}
    >
      <CardContent sx={{ p: 2.25, position: 'relative', '&:last-child': { pb: 2.25 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, fontSize: '11px' }}>
            {kpi.label}
          </Typography>
          {kpi.hint ? (
            <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.disabled', fontStyle: 'italic' }}>
              {kpi.hint}
            </Typography>
          ) : null}
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.75, lineHeight: 1.1, color: kpi.tone === 'negative' || kpi.tone === 'warning' ? accent : 'text.primary' }}>
          {kpi.value}
        </Typography>
        {kpi.sub ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {kpi.sub}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function KpiRow({ kpis, loading }: { kpis: Kpi[]; loading?: boolean }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(180px, 1fr))' }, gap: 1.5 }}>
      {kpis.map((kpi, i) => (
        <KpiCard key={i} kpi={kpi} loading={loading} />
      ))}
    </Box>
  );
}
